import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  App,
  Avatar,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { usePermission } from '@/hooks/usePermission';
import { MarkdownDisplay, RichTextEditor, withDevModalFilePath } from '@/components/common';
import { formatDateWithFallback } from '@/utils/dateFormat';
import { PAGE_MEMO_REACTION_EMOJIS } from '@shared/types/page-memo.types';
import type { PageMemoBundle } from '@shared/types/page-memo.types';

const { Text } = Typography;
const { TextArea } = Input;
const { Drawer } = withDevModalFilePath('client/src/components/memo/PageMemoDrawer.tsx');

export const PAGE_MEMO_DRAWER_WIDTH = 480;
export const PAGE_MEMO_DRAWER_MIN_WIDTH = 360;
export const PAGE_MEMO_DRAWER_MAX_WIDTH = 960;
export const PAGE_MEMO_DRAWER_MIN_CONTENT_WIDTH = 320;

interface PageMemoDrawerProps {
  open: boolean;
  onClose: () => void;
  pageKey: string | null;
  pageLabel?: string;
  width?: number;
  onWidthChange?: (nextWidth: number) => void;
  resizable?: boolean;
}

export function PageMemoDrawer({
  open,
  onClose,
  pageKey,
  pageLabel,
  width,
  onWidthChange,
  resizable = true,
}: PageMemoDrawerProps): JSX.Element {
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const { isManagerOrAdmin } = usePermission();
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const [commonEditing, setCommonEditing] = useState(false);
  const [commonDraft, setCommonDraft] = useState('');
  const [personalEditing, setPersonalEditing] = useState(false);
  const [personalDraft, setPersonalDraft] = useState('');
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');

  const { data, isLoading, refetch, isFetching } = trpc.pageMemo.getPageMemo.useQuery(
    { pageKey: pageKey ?? '' },
    {
      enabled: open && Boolean(pageKey),
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
  const memoBundle = data as PageMemoBundle | undefined;

  const upsertCommonMemoMutation = trpc.pageMemo.upsertCommonMemo.useMutation({
    onSuccess: () => {
      message.success('共通メモを保存しました');
      setCommonEditing(false);
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || '共通メモの保存に失敗しました');
    },
  });

  const upsertPersonalMemoMutation = trpc.pageMemo.upsertPersonalMemo.useMutation({
    onSuccess: () => {
      message.success('個人メモを保存しました');
      setPersonalEditing(false);
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || '個人メモの保存に失敗しました');
    },
  });

  const createCommentMutation = trpc.pageMemo.createComment.useMutation({
    onSuccess: () => {
      message.success('コメントを投稿しました');
      setNewComment('');
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの投稿に失敗しました');
    },
  });

  const updateCommentMutation = trpc.pageMemo.updateComment.useMutation({
    onSuccess: () => {
      message.success('コメントを更新しました');
      setEditingCommentId(null);
      setEditingCommentContent('');
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの更新に失敗しました');
    },
  });

  const deleteCommentMutation = trpc.pageMemo.deleteComment.useMutation({
    onSuccess: () => {
      message.success('コメントを削除しました');
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'コメントの削除に失敗しました');
    },
  });

  const toggleReactionMutation = trpc.pageMemo.toggleReaction.useMutation({
    onSuccess: () => {
      void refetch();
    },
    onError: (error) => {
      message.error(error.message || 'リアクションの更新に失敗しました');
    },
  });

  useEffect(() => {
    if (!open) {
      setCommonEditing(false);
      setPersonalEditing(false);
      setEditingCommentId(null);
      setEditingCommentContent('');
      setNewComment('');
    }
  }, [open]);

  useEffect(() => {
    if (!pageKey) {
      return;
    }
    setCommonEditing(false);
    setPersonalEditing(false);
    setEditingCommentId(null);
    setEditingCommentContent('');
    setNewComment('');
  }, [pageKey]);

  useEffect(() => {
    if (!commonEditing) {
      setCommonDraft(memoBundle?.commonMemo?.content ?? '');
    }
  }, [memoBundle?.commonMemo?.content, commonEditing]);

  useEffect(() => {
    if (!personalEditing) {
      setPersonalDraft(memoBundle?.personalMemo?.content ?? '');
    }
  }, [memoBundle?.personalMemo?.content, personalEditing]);

  const reactionItems = useMemo(() => {
    const reactionMap = new Map<
      string,
      {
        count: number;
        users: { id?: string; name?: string | null; email?: string | null; avatarUrl?: string | null }[];
      }
    >();
    memoBundle?.reactions.forEach((reaction) => {
      reactionMap.set(reaction.emoji, { count: reaction.count, users: reaction.users });
    });

    return PAGE_MEMO_REACTION_EMOJIS.map((emoji) => {
      const reaction = reactionMap.get(emoji);
      const users = reaction?.users ?? [];
      const reactedByMe = users.some((u) => u.id && u.id === user?.id);
      const names = users
        .map((u) => u.name || u.email || '不明なユーザー')
        .filter(Boolean);
      const tooltip = names.length > 0
        ? names.slice(0, 6).join(', ') + (names.length > 6 ? ' ほか' : '')
        : 'まだありません';

      return {
        emoji,
        count: reaction?.count ?? 0,
        reactedByMe,
        tooltip,
      };
    });
  }, [memoBundle?.reactions, user?.id]);

  const handleSaveCommonMemo = () => {
    if (!pageKey) return;
    upsertCommonMemoMutation.mutate({ pageKey, content: commonDraft });
  };

  const handleSavePersonalMemo = () => {
    if (!pageKey) return;
    upsertPersonalMemoMutation.mutate({ pageKey, content: personalDraft });
  };

  const handleCreateComment = () => {
    if (!pageKey || !newComment.trim()) return;
    createCommentMutation.mutate({ pageKey, content: newComment.trim() });
  };

  const handleUpdateComment = (commentId: string) => {
    if (!editingCommentContent.trim()) return;
    updateCommentMutation.mutate({ commentId, content: editingCommentContent.trim() });
  };

  const handleDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate({ commentId });
  };

  const startEditingComment = (commentId: string, content: string) => {
    setEditingCommentId(commentId);
    setEditingCommentContent(content);
  };

  const cancelEditingComment = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleToggleReaction = (emoji: string) => {
    if (!pageKey) return;
    toggleReactionMutation.mutate({ pageKey, emoji: emoji as (typeof PAGE_MEMO_REACTION_EMOJIS)[number] });
  };

  const isBusy = isLoading || isFetching;
  const comments = memoBundle?.comments ?? [];
  const resolvedWidth = width ?? PAGE_MEMO_DRAWER_WIDTH;
  const canResize = resizable && Boolean(onWidthChange);

  const clampWidth = useCallback((value: number) => {
    const minWidth = PAGE_MEMO_DRAWER_MIN_WIDTH;
    if (typeof window === 'undefined') {
      return Math.min(Math.max(value, minWidth), PAGE_MEMO_DRAWER_MAX_WIDTH);
    }
    const maxWidth = Math.min(
      PAGE_MEMO_DRAWER_MAX_WIDTH,
      window.innerWidth - PAGE_MEMO_DRAWER_MIN_CONTENT_WIDTH
    );
    return Math.min(Math.max(value, minWidth), Math.max(minWidth, maxWidth));
  }, []);

  const applyWidth = useCallback((value: number) => {
    if (!onWidthChange) return;
    const next = clampWidth(value);
    if (next !== width) {
      onWidthChange(next);
    }
  }, [clampWidth, onWidthChange, width]);

  const handleResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!canResize) return;
    event.preventDefault();
    event.stopPropagation();
    setIsResizing(true);
    dragStateRef.current = { startX: event.clientX, startWidth: resolvedWidth };
  }, [canResize, resolvedWidth]);

  useEffect(() => {
    if (!isResizing) return;
    const handlePointerMove = (event: PointerEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      const delta = state.startX - event.clientX;
      applyWidth(state.startWidth + delta);
    };
    const handlePointerUp = () => {
      setIsResizing(false);
      dragStateRef.current = null;
    };
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
    };
  }, [applyWidth, isResizing]);

  useEffect(() => {
    if (!open || !onWidthChange) return;
    applyWidth(resolvedWidth);
  }, [open, onWidthChange, applyWidth, resolvedWidth]);

  useEffect(() => {
    if (!open || !onWidthChange) return;
    const handleResize = () => {
      applyWidth(resolvedWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open, onWidthChange, applyWidth, resolvedWidth]);

  return (
    <Drawer
      title={
        <Space>
          <FormOutlined />
          <span>{pageLabel ?? 'ページメモ'}</span>
        </Space>
      }
      open={open}
      onClose={onClose}
      placement="right"
      width={resolvedWidth}
      destroyOnClose
      mask={false}
      maskClosable={false}
      push={false}
      styles={{ body: { padding: 16, overflowY: 'auto', height: '100%', position: 'relative' } }}
    >
      {canResize && (
        <div
          className={`page-memo-drawer-resizer${isResizing ? ' is-resizing' : ''}`}
          onPointerDown={handleResizeStart}
          role="separator"
          aria-label="メモ幅調整"
          aria-orientation="vertical"
          data-testid="memo-drawer-resizer"
        />
      )}
      {!pageKey ? (
        <Empty description="メモ対象のページが見つかりません" />
      ) : (
        <Spin spinning={isBusy}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
              size="small"
              title="共通メモ"
              extra={
                <Space size={8}>
                  <Link
                    to="/app/memos"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    <Button size="small" type="link" icon={<SettingOutlined />}>
                      一元メモ設定へ
                    </Button>
                  </Link>
                  {isManagerOrAdmin && (
                    commonEditing ? (
                      <>
                        <Button
                          type="primary"
                          size="small"
                          icon={<SaveOutlined />}
                          loading={upsertCommonMemoMutation.isPending}
                          onClick={handleSaveCommonMemo}
                        >
                          保存
                        </Button>
                        <Button
                          size="small"
                          icon={<StopOutlined />}
                          onClick={() => setCommonEditing(false)}
                        >
                          キャンセル
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setCommonEditing(true)}
                      >
                        編集
                      </Button>
                    )
                  )}
                </Space>
              }
            >
              {commonEditing ? (
                <RichTextEditor
                  value={commonDraft}
                  onChange={setCommonDraft}
                  minHeight={180}
                />
              ) : memoBundle?.commonMemo?.content ? (
                <MarkdownDisplay content={memoBundle.commonMemo.content} />
              ) : (
                <Text type="secondary">まだ共通メモはありません。</Text>
              )}
            </Card>

            <Card size="small" title="リアクション">
              <Space size={8} wrap>
                {reactionItems.map((reaction) => (
                  <Tooltip key={reaction.emoji} title={reaction.tooltip}>
                    <Button
                      size="small"
                      type={reaction.reactedByMe ? 'primary' : 'default'}
                      onClick={() => handleToggleReaction(reaction.emoji)}
                    >
                      {reaction.emoji} {reaction.count}
                    </Button>
                  </Tooltip>
                ))}
              </Space>
            </Card>

            <Card
              size="small"
              title={
                <Space>
                  <CommentOutlined />
                  <span>コメント</span>
                  {comments.length > 0 && <Text type="secondary">({comments.length})</Text>}
                </Space>
              }
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <TextArea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="コメントを入力..."
                    autoSize={{ minRows: 2, maxRows: 4 }}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleCreateComment}
                    loading={createCommentMutation.isPending}
                    disabled={!newComment.trim()}
                  >
                    投稿
                  </Button>
                </Space>

                {comments.length === 0 ? (
                  <Empty description="コメントはまだありません" />
                ) : (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {comments.map((comment) => {
                      const isOwner = comment.userId === user?.id;
                      const canManageComment = isOwner || isManagerOrAdmin;
                      const isEditing = editingCommentId === comment.id;
                      const displayName = comment.user?.name || comment.user?.email || '不明なユーザー';

                      return (
                        <Card key={comment.id} size="small" style={{ background: '#fafafa' }}>
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Space size={8} align="center">
                              <Avatar
                                size={32}
                                src={comment.user?.avatarUrl ?? undefined}
                                icon={!comment.user?.avatarUrl && <UserOutlined />}
                              />
                              <Space direction="vertical" size={0}>
                                <Text>{displayName}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {formatDateWithFallback(comment.createdAt, { showTime: true })}
                                </Text>
                              </Space>
                            </Space>

                            {isEditing ? (
                              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <TextArea
                                  value={editingCommentContent}
                                  onChange={(e) => setEditingCommentContent(e.target.value)}
                                  autoSize={{ minRows: 2, maxRows: 4 }}
                                />
                                <Space>
                                  <Button
                                    type="primary"
                                    size="small"
                                    loading={updateCommentMutation.isPending}
                                    onClick={() => handleUpdateComment(comment.id)}
                                  >
                                    保存
                                  </Button>
                                  <Button size="small" onClick={cancelEditingComment}>
                                    キャンセル
                                  </Button>
                                </Space>
                              </Space>
                            ) : (
                              <Text style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Text>
                            )}

                            {canManageComment && !isEditing && (
                              <Space size={8}>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => startEditingComment(comment.id, comment.content)}
                                >
                                  編集
                                </Button>
                                <Popconfirm
                                  title="コメントを削除しますか？"
                                  okText="削除"
                                  cancelText="キャンセル"
                                  onConfirm={() => handleDeleteComment(comment.id)}
                                >
                                  <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleteCommentMutation.isPending}
                                  >
                                    削除
                                  </Button>
                                </Popconfirm>
                              </Space>
                            )}
                          </Space>
                        </Card>
                      );
                    })}
                  </Space>
                )}
              </Space>
            </Card>

            <Card
              size="small"
              title="個人メモ"
              extra={
                <Space size={8}>
                  {personalEditing ? (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        loading={upsertPersonalMemoMutation.isPending}
                        onClick={handleSavePersonalMemo}
                      >
                        保存
                      </Button>
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        onClick={() => setPersonalEditing(false)}
                      >
                        キャンセル
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setPersonalEditing(true)}
                    >
                      編集
                    </Button>
                  )}
                </Space>
              }
            >
              {personalEditing ? (
                <RichTextEditor
                  value={personalDraft}
                  onChange={setPersonalDraft}
                  minHeight={160}
                />
              ) : memoBundle?.personalMemo?.content ? (
                <MarkdownDisplay content={memoBundle.personalMemo.content} />
              ) : (
                <Text type="secondary">まだ個人メモはありません。</Text>
              )}
            </Card>
          </Space>
        </Spin>
      )}
      {canResize && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .page-memo-drawer-resizer {
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                width: 10px;
                cursor: col-resize;
                z-index: 10;
                touch-action: none;
              }
              .page-memo-drawer-resizer::after {
                content: '';
                position: absolute;
                top: 0;
                left: 4px;
                bottom: 0;
                width: 2px;
                border-radius: 1px;
                background: rgba(0, 0, 0, 0.12);
              }
              .page-memo-drawer-resizer:hover::after,
              .page-memo-drawer-resizer.is-resizing::after {
                background: rgba(0, 0, 0, 0.35);
              }
              html[data-app-theme='dark'] .page-memo-drawer-resizer::after {
                background: rgba(255, 255, 255, 0.2);
              }
              html[data-app-theme='dark'] .page-memo-drawer-resizer:hover::after,
              html[data-app-theme='dark'] .page-memo-drawer-resizer.is-resizing::after {
                background: rgba(255, 255, 255, 0.45);
              }
            `,
          }}
        />
      )}
    </Drawer>
  );
}
